/**
 * 短视频文案提取（抖音等分享链接 → 无水印解析 + AI 语音识别）
 * 对接后端 POST /douyin_extract_text（对齐 https://github.com/yzfly/douyin-mcp-server ）
 * 参数 content / share_url / url：分享链接或含链接的口令文案
 *
 * 注意：Dingo / Laravel 可能多层包装 data，需深度解包。
 */

import api from '@/utils/api'
import { getDouyinCookie } from '@/utils/douyinCookieStore'

function buildDouyinRequestBody(content, extra = {}) {
  const body = {
    content,
    share_url: content,
    url: content,
    ...extra
  }
  const cookie = getDouyinCookie()
  if (cookie) {
    body.douyin_cookie = cookie
  }
  return body
}

function assertApiOk(res) {
  if (res == null || typeof res !== 'object') return
  const c = res.code
  if (c === undefined || c === null) return
  const n = Number(c)
  if (n === 0 || n === 200) return
  const msg = res.message || res.msg || res.error || `接口 code=${c}`
  throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
}

/** 剥掉 Dingo / Laravel 多余 data 壳，直到触达带文案字段的对象 */
function unwrapPayload(res) {
  let cur = res
  for (let i = 0; i < 5; i++) {
    if (!cur || typeof cur !== 'object' || Array.isArray(cur)) break
    if (typeof cur.text === 'string' && cur.text.trim()) return cur
    if (typeof cur.content === 'string' && cur.content.trim() && !cur.data) return cur
    const d = cur.data
    if (d == null) break
    if (typeof d === 'string' && d.trim()) {
      return { text: d.trim(), ...cur }
    }
    if (typeof d === 'object' && !Array.isArray(d)) {
      cur = d
      continue
    }
    break
  }
  return cur && typeof cur === 'object' ? cur : {}
}

function pickTextFromObj(obj) {
  if (!obj || typeof obj !== 'object') return ''
  for (const k of ['text', 'content', 'wenan', 'copy', 'transcript', 'result']) {
    const v = obj[k]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return ''
}

function pickText(res) {
  const payload = unwrapPayload(res)
  const t = pickTextFromObj(payload)
  if (t) return t
  // 兼容：原文案在 res.data 字符串 / 顶层
  if (res && typeof res === 'object') {
    if (typeof res.data === 'string' && res.data.trim()) return res.data.trim()
    return pickTextFromObj(res)
  }
  return ''
}

function pickMeta(res) {
  const d = unwrapPayload(res)
  const title = String(d.title || d.desc || d.video_title || '').trim()
  const downloadUrl = String(
    d.download_url || d.video_url || d.play_url || d.url || ''
  ).trim()
  const videoId = String(d.video_id || d.aweme_id || '').trim()
  return { title, downloadUrl, videoId }
}

/**
 * 从分享链接/粘贴文案中提取短视频文案
 * @param {string} shareContent 抖音等短视频分享链接，或包含链接的完整分享文案
 * @returns {Promise<{ text: string, title: string, downloadUrl: string, videoId: string, raw: any }>}
 */
export async function extractShortVideoCopy(shareContent) {
  const content = String(shareContent || '').trim()
  if (!content) throw new Error('请输入短视频分享链接')

  const res = await api.post('/douyin_extract_text', buildDouyinRequestBody(content))
  assertApiOk(res)
  // 内层也可能带 code
  assertApiOk(unwrapPayload(res))

  const text = pickText(res)
  if (!text) {
    const err =
      (res && (res.error || res.message || (res.msg !== 'success' ? res.msg : ''))) ||
      '文案提取失败：响应中未找到 text 字段'
    throw new Error(String(err))
  }

  const meta = pickMeta(res)
  return { text, ...meta, raw: res }
}

/** 仅解析视频信息与无水印下载链接（无需 ASR Key） */
export async function parseShortVideoInfo(shareContent) {
  const content = String(shareContent || '').trim()
  if (!content) throw new Error('请输入短视频分享链接')
  const res = await api.post('/douyin_video_info', buildDouyinRequestBody(content))
  assertApiOk(res)
  const meta = pickMeta(res)
  if (!meta.downloadUrl && !meta.videoId) {
    const err = (res && (res.error || res.message || res.msg)) || '解析视频信息失败'
    throw new Error(String(err))
  }
  return { ...meta, raw: res }
}

/** 从任意文本中抓取第一个 http(s) 链接（分享口令里常夹杂说明文字） */
export function extractFirstHttpUrl(text) {
  const s = String(text || '')
  const m = s.match(/https?:\/\/[^\s<>"']+/i)
  return m ? m[0].replace(/[),.;，。；]+$/, '') : ''
}
